import { useEffect, useState } from 'react'
import {
	EuiFlexGroup,
	EuiFlexItem,
	EuiFormRow,
	EuiFieldNumber,
	EuiButtonIcon,
} from '@equipmentshare/ds2'

import CurrencyField from 'components/CurrencyField/currencyField'
import PartPicker from 'components/PartPicker'

import { AddToInventoryItemFormState } from '../AddToInventoryForm'
import { Part, useStorePartQuantityCostLazyQuery } from '../../../../graphql'
import { checkForUnexpectedCost, SetUnexpectedCost } from '../AddToInventoryForm/helpers/validateForm'

import styles from './itemRow.module.css'

export type ItemRowProps = {
	itemFormState: AddToInventoryItemFormState,
	setItemFormState: ( itemFormState: AddToInventoryItemFormState ) => void,
	setUnexpectedCost: SetUnexpectedCost,
	onDeleteClick: () => void,
	storeId?: number,
}

export default function ItemRow( { itemFormState, setItemFormState, onDeleteClick, storeId, setUnexpectedCost }: ItemRowProps ) {
	const [ loadStorePart, { loading } ] = useStorePartQuantityCostLazyQuery( { errorPolicy: 'ignore' } )
	const [ storePartCost, setStorePartCost ] = useState<number>()

	const onChangeProduct = ( _?: number, item?: Part ) => {
		setItemFormState( {
			...itemFormState,
			item,
			validation: {
				...itemFormState.validation,
				item: { isInvalid: false },
			}
		} )
	}

	const onChangeQuantity = ( event: React.ChangeEvent<HTMLInputElement> ) => setItemFormState( {
		...itemFormState,
		quantity: event.currentTarget.value,
		validation: {
			...itemFormState.validation,
			quantity: { isInvalid: false },
		}
	} )

	const onChangeCost = ( cost: string, triggerValidation?: boolean ) => {
		if ( cost !== itemFormState.cost ) {
			const newItem = {
				...itemFormState,
				cost,
			}

			if ( triggerValidation ) {
				newItem.validation = {
					...itemFormState.validation,
					cost: { isInvalid: false }
				}
			}

			setItemFormState( newItem )
		}
	}

	const onBlurCost = () => {
		if ( storePartCost ) {
			const costValidation = checkForUnexpectedCost( itemFormState, storePartCost, setUnexpectedCost )
			setItemFormState( {
				...itemFormState,
				validation: {
					...itemFormState.validation,
					cost: costValidation,
				}
			} )
		}
	}

	useEffect( () => {
		( async() => {
			if ( storeId && itemFormState.item ) {
				const { data } = await loadStorePart( { variables: { storeId, partId: itemFormState.item.id } } )
				if ( data?.storePart ) {
					setStorePartCost( data.storePart.cost ?? undefined )
					onChangeCost( data.storePart.cost?.toString() ?? '' )
				} else {
					onChangeCost( '' )
				}
			}
		} )()
	}, [ storeId, itemFormState.item ] )

	return (
		<EuiFlexGroup alignItems="flexStart" gutterSize="m" wrap>
			<EuiFlexItem className={styles.item}>
				<PartPicker
					className={styles.partPicker}
					onChange={onChangeProduct}
					selectedPart={itemFormState.item}
					label={<>Item <span className="required">*</span></>}
					formRowProps={{
						isInvalid: itemFormState.validation.item.isInvalid,
						error: itemFormState.validation.item.error,
					}}
					isInvalid={itemFormState.validation.item.isInvalid}
				/>
			</EuiFlexItem>
			<EuiFlexItem>
				<EuiFlexGroup alignItems="flexStart" gutterSize="m">
					<EuiFlexItem grow={false} className={styles.quantity}>
						<EuiFormRow
							label={<>Quantity <span className="required">*</span></>}
							isInvalid={itemFormState.validation.quantity.isInvalid}
							error={itemFormState.validation.quantity.error}
						>
							<EuiFieldNumber
								value={itemFormState.quantity}
								onChange={onChangeQuantity}
								isInvalid={itemFormState.validation.quantity.isInvalid}
							/>
						</EuiFormRow>
					</EuiFlexItem>
					<EuiFlexItem grow={false} className={styles.cost}>
						<EuiFormRow
							label={<>Cost <span className="required">*</span></>}
							isInvalid={itemFormState.validation.cost.isInvalid}
							error={itemFormState.validation.cost.error}
						>
							<CurrencyField
								id="costField"
								value={itemFormState.cost}
								onRawChange={onChangeCost}
								isLoading={loading}
								onBlur={onBlurCost}
								isInvalid={itemFormState.validation.cost.isInvalid}
							/>
						</EuiFormRow>
					</EuiFlexItem>
					<EuiFlexItem>
						<EuiFormRow hasEmptyLabelSpace>
							<EuiButtonIcon
								display="base"
								size="m"
								color="text"
								iconType="trash"
								onClick={onDeleteClick}
								aria-label="Delete row"
							/>
						</EuiFormRow>
					</EuiFlexItem>
				</EuiFlexGroup>
			</EuiFlexItem>
		</EuiFlexGroup>
	)
}
